import { AxiosError } from "axios";
import { IErrorResponse } from "../../state/querys/querys.enum";
import {
  INotificationKeysValues,
  INotificationManagement,
} from "../../models/Interface/NotificationManagement.interface";

/**
 * Validation errors (400) arrive as an array of constraint messages, everything
 * else as a single `message` string.
 */
export const getApiErrorMessage = (
  error?: AxiosError<IErrorResponse> | null,
  fallback = "Something went wrong."
): string => {
  const message = (error?.response?.data as { message?: unknown })?.message;

  if (Array.isArray(message)) {
    return message.filter(Boolean).join(" · ");
  }
  if (typeof message === "string" && message.trim()) {
    return message;
  }
  return fallback;
};

export interface INotificationTypeGroup {
  notificationType: string;
  records: INotificationManagement[];
}

/** Records sharing a type are its key sets, not duplicates — group, never dedupe. */
export const groupDefinitionsByType = (
  definitions: INotificationManagement[]
): INotificationTypeGroup[] => {
  const byType = new Map<string, INotificationManagement[]>();

  definitions.forEach((definition) => {
    const bucket = byType.get(definition.notificationType) ?? [];
    bucket.push(definition);
    byType.set(definition.notificationType, bucket);
  });

  return Array.from(byType.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([notificationType, records]) => ({
      notificationType,
      records: [...records].sort((a, b) => a.variant - b.variant),
    }));
};

export interface IPreviewRow {
  label: string;
  keyName: string;
  value: string;
  /** Rendered as an image thumbnail when the value is a URL. */
  isUrl?: boolean;
}

export const buildPreviewRows = (
  entry: INotificationKeysValues
): IPreviewRow[] => [
  { label: "Title", keyName: entry.titleKey, value: entry.title },
  {
    label: "Description",
    keyName: entry.descriptionKey,
    value: entry.description,
  },
  { label: "Image", keyName: entry.urlKey, value: entry.url, isUrl: true },
  { label: "Fallback", keyName: entry.fallbackKey, value: entry.fallback },
  { label: "Deeplink", keyName: entry.deeplinkKey, value: entry.deeplink },
];

/**
 * An empty value means the key is absent from that language's localization
 * file. An empty *key* means the type simply has no such key (e.g. deeplink).
 */
export const isMissingValue = (row: IPreviewRow): boolean =>
  !!row.keyName && !row.value;

export const countMissingValues = (entry: INotificationKeysValues): number =>
  buildPreviewRows(entry).filter(isMissingValue).length;

export const groupKeysValuesByType = (
  keysValues: INotificationKeysValues[]
): { notificationType: string; entries: INotificationKeysValues[] }[] => {
  const byType = new Map<string, INotificationKeysValues[]>();

  keysValues.forEach((entry) => {
    const bucket = byType.get(entry.notificationType) ?? [];
    bucket.push(entry);
    byType.set(entry.notificationType, bucket);
  });

  return Array.from(byType.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([notificationType, entries]) => ({
      notificationType,
      entries: [...entries].sort((a, b) => a.variant - b.variant),
    }));
};
